'use client';

import type {FC} from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import style from './style.module.scss';

// Map packages
import GoogleMapReact from 'google-map-react';

// Helpers
import {
    applyViewport,
    BUCHAREST_FALLBACK_ZOOM,
    clearRoute,
    drawRouteToStation,
    GOOGLE_MAP_API_KEY,
    initializeMap,
    MAP_LOCATIONS,
    MAP_STYLES,
    SIDE_LIST_LIMIT,
    addOffsetToDuplicateCoordinates,
    getConnectorIcon,
    getDistanceKm,
    getDistanceLabel,
    getDistanceReferenceContext,
    getFilterPayload,
    getFilterPayloadWithOverrides,
    getGoogleMapsDirectionsUrl,
    getMapBoundsSnapshot,
    getSearchZoomForTypes,
    getStatusClassName,
    handleClusterZoom,
    handleMapChange,
    transformStations
} from './helpers';
import {
    applySearchResult,
    fetchGoogleSuggestions,
    geocodeSearchSelection,
    runManualViewportUpdate,
} from './search-helpers';
import { useDinamicMapUiEffects } from './useDinamicMapUiEffects';
import {
    insertInitialSidebarStation,
    preserveSummaryStationStatus,
    refreshSidebarStations,
} from './sidebar-controller';
import { loadStationDetails } from './station-details-controller';
import { handleStationSelect } from './station-selection-controller';
import type {
    MapProps,
    SearchLocationDetails,
    SearchSuggestion,
    SidebarPopulationMode
} from './interface';

// Components
import Marker from './components/Marker';
import FilterDrawer from './components/FilterDrawer';
import EmptySidebarState from './components/EmptySidebarState';
import MapSearchBar from './components/MapSearchBar';
import MapTopBar from './components/MapTopBar';
import StationCard from './components/StationCard';
import StationModal from './components/StationModal';
import {getChargingLocationById, getClusteredChargingLocations} from '../../utils/api';
import type {ChargingLocationFilters} from '../../utils/api';
import classnames from "classnames";
import {debounce} from "lodash";
import {STATUS_LABEL} from "@/app/[lang]/components/DinamicMap/components/Charger/Charger";
import Image from "@/app/[lang]/components/shared/SmartImage";
import {Skeleton} from "antd";
import chargerType2 from "@/app/[lang]/components/DinamicMap/assets/type-2.svg";
import chargerCcsCombo2 from "@/app/[lang]/components/DinamicMap/assets/ccs-combo-2.svg";
import chargerChademo from "@/app/[lang]/components/DinamicMap/assets/chademo.svg";
import stationIconAsset from "@/app/[lang]/components/DinamicMap/assets/ppc-icon.png";

const DinamicMap: FC<MapProps> = ({DEFAULT_CENTER = MAP_LOCATIONS.Bucharest, DEFAULT_ZOOM = 6.5, MAX_ZOOM = 20}) => {
    const geolocationFallbackCenter = MAP_LOCATIONS.Bucharest;
    const mapRef = useRef<google.maps.Map>();
    const mapsRef = useRef<typeof google.maps>();
    const searchPinMarkerRef = useRef<google.maps.Marker | null>(null);
    const directionsServiceRef = useRef<google.maps.DirectionsService | null>(null);
    const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
    const searchBlurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const suggestionRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const sidebarListRef = useRef<HTMLDivElement | null>(null);
    const sidebarCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const placesAutocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
    const autocompleteRequestIdRef = useRef(0);

    const [openMarker, setOpenMarker] = useState<string | number | null>(null);
    const [expandedSidebarStationId, setExpandedSidebarStationId] = useState<string | number | null>(null);
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
    const [bounds, setBounds] = useState<[number, number, number, number] | undefined>(undefined);
    const [center, setCenter] = useState<GoogleMapReact.Coords>(DEFAULT_CENTER);
    const [userLocation, setUserLocation] = useState<GoogleMapReact.Coords | null>(null);
    const [isInitialViewportResolved, setIsInitialViewportResolved] = useState(false);
    const [mapItems, setMapItems] = useState<any[]>([]);
    const [stations, updateStations] = useState<any[]>([]);
    const [baseSidebarStations, setBaseSidebarStations] = useState<any[]>([]);
    const [selectedSidebarStation, setSelectedSidebarStation] = useState<any | null>(null);
    const [stationDetailsById, setStationDetailsById] = useState<Record<string, any>>({});
    const [stationDetailsLoadingById, setStationDetailsLoadingById] = useState<Record<string, boolean>>({});
    const [searchValue, setSearchValue] = useState('');
    const [searchSuggestions, setSearchSuggestions] = useState<SearchSuggestion[]>([]);
    const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
    const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState(-1);
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [searchPinLocation, setSearchPinLocation] = useState<GoogleMapReact.Coords | null>(null);
    const [showLocationSearchEmptyState, setShowLocationSearchEmptyState] = useState(false);
    const [showAvailability, setShowAvailability] = useState(false);
    const [showStandard, setShowStandard] = useState(false);
    const [showRapid, setShowRapid] = useState(false);
    const [showType2, setShowType2] = useState(false);
    const [showCcsCombo2, setShowCcsCombo2] = useState(false);
    const [showChademo, setShowChademo] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState<ChargingLocationFilters>({
        availability: false,
        standard: false,
        rapid: false,
        type2: false,
        ccsCombo2: false,
        chademo: false
    });
    const appliedFiltersRef = useRef<ChargingLocationFilters>(appliedFilters);
    const stationDetailsByIdRef = useRef<Record<string, any>>(stationDetailsById);
    const stationDetailsLoadingByIdRef = useRef<Record<string, boolean>>(stationDetailsLoadingById);
    const sidebarBatchIdRef = useRef(0);
    const sidebarBatchStartedAtRef = useRef<number | null>(null);
    const sidebarDetailsDurationRef = useRef(0);
    const sidebarDetailsCompletedCountRef = useRef(0);
    const sidebarDetailsTargetCountRef = useRef(0);
    const sidebarInitializedRef = useRef(false);
    const initialSidebarOrderRef = useRef<Array<string | number>>([]);
    const sidebarPopulationModeRef = useRef<SidebarPopulationMode>('nearest');
    const skipViewportRefreshCountRef = useRef(0);
    const requestZoomOverrideRef = useRef<number | null>(null);

    useEffect(() => {
        if (!GOOGLE_MAP_API_KEY) {
            console.error('NEXT_PUBLIC_GOOGLE_DYNAMIC_MAP_API_KEY is not configured.');
        }
    }, []);

    const requestUserLocation = (): Promise<GoogleMapReact.Coords | null> =>
        new Promise((resolve) => {
            navigator?.geolocation.getCurrentPosition(
                ({coords}) => {
                    if (!coords) {
                        resolve(null);
                        return;
                    }
                    const location = {lat: coords.latitude, lng: coords.longitude};
                    setUserLocation(location);
                    resolve(location);
                },
                () => resolve(null),
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 60000
                }
            );
        });

    const getNearestStations = (
        sourceStations: any[],
        referencePoint: GoogleMapReact.Coords,
        limit: number
    ) =>
        [...sourceStations]
            .sort((leftStation, rightStation) =>
                getDistanceKm(referencePoint, leftStation) - getDistanceKm(referencePoint, rightStation)
            )
            .slice(0, limit);

    const loadSidebarStationDetails = (
        stationId: string | number,
        batchId: number = sidebarBatchIdRef.current,
        summaryStation?: any
    ) =>
        loadStationDetails({
            stationId,
            batchId,
            stationDetailsByIdRef,
            stationDetailsLoadingByIdRef,
            setStationDetailsLoadingById,
            getChargingLocationById,
            transformStations,
            summaryStation,
            stations,
            preserveSummaryStationStatus,
            sidebarBatchIdRef,
            setStationDetailsById,
            sidebarDetailsDurationRef,
            sidebarDetailsCompletedCountRef,
        });

    const refreshSidebarStationsController = (
        nextStations: any[],
        mode: 'nearest' | 'cluster-order' | 'station-proximity',
        referencePoint: GoogleMapReact.Coords,
        options?: { preserveActiveStation?: boolean }
    ) =>
        refreshSidebarStations({
            nextStations,
            mode,
            referencePoint,
            options,
            getNearestStations,
            sideListLimit: SIDE_LIST_LIMIT,
            userLocation,
            geolocationFallbackCenter,
            sidebarInitializedRef,
            sidebarPopulationModeRef,
            initialSidebarOrderRef,
            sidebarBatchIdRef,
            setStationDetailsById,
            setStationDetailsLoadingById,
            stationDetailsByIdRef,
            stationDetailsLoadingByIdRef,
            sidebarBatchStartedAtRef,
            sidebarDetailsDurationRef,
            sidebarDetailsCompletedCountRef,
            sidebarDetailsTargetCountRef,
            setShowLocationSearchEmptyState,
            setBaseSidebarStations,
            setSelectedSidebarStation,
            setExpandedSidebarStationId,
            setOpenMarker,
            clearRoute: () => clearRoute(directionsRendererRef.current),
            loadStationDetails: loadSidebarStationDetails,
            insertInitialSidebarStation: (detailedStation) =>
                insertInitialSidebarStation({
                    station: detailedStation,
                    initialSidebarOrderRef,
                    setBaseSidebarStations,
                    sideListLimit: SIDE_LIST_LIMIT,
                }),
        });

    const handleStationSelectController = async (station: any) => {
        const routeResult = await handleStationSelect({
            station,
            stations,
            setOpenMarker,
            setExpandedSidebarStationId,
            refreshSidebarStations: refreshSidebarStationsController,
            loadSidebarStationDetails,
            sidebarBatchId: sidebarBatchIdRef.current,
            setSelectedSidebarStation,
            searchPinLocation,
            userLocation,
            requestUserLocation,
            maps: mapsRef.current,
            map: mapRef.current,
            directionsService: directionsServiceRef.current,
            directionsRenderer: directionsRendererRef.current,
        });

        directionsServiceRef.current = routeResult.directionsService;
        directionsRendererRef.current = routeResult.directionsRenderer;
    };

    const setMapSearchViewport = async (
        nextCenter: GoogleMapReact.Coords,
        nextZoom: number,
        skippedRefreshCount: number = 0
    ): Promise<{
        center: GoogleMapReact.Coords;
        bounds: [number, number, number, number];
        zoom: number;
    } | null> => {
        const map = mapRef.current;

        if (!map) {
            return null;
        }

        if (skippedRefreshCount > 0) {
            skipViewportRefreshCountRef.current += skippedRefreshCount;
        }

        await new Promise<void>((resolve) => {
            const listener = map.addListener('idle', () => {
                listener.remove();
                resolve();
            });

            map.panTo(nextCenter);
            map.setZoom(nextZoom);
        });

        return {
            center: nextCenter,
            bounds: getMapBoundsSnapshot(map, nextCenter),
            zoom: map.getZoom() ?? 13,
        };
    };

    const updateMarkers = (
        search: string,
        filters: ChargingLocationFilters,
        nextCenter: GoogleMapReact.Coords,
        nextBounds?: [number, number, number, number],
        nextZoom: number = DEFAULT_ZOOM,
        sidebarMode: SidebarPopulationMode = sidebarInitializedRef.current
            ? 'preserve'
            : sidebarPopulationModeRef.current
    ) => {
        const hasSearch = !!search.trim();
        if (hasSearch) {
            setIsSearchLoading(true);
            setShowSearchSuggestions(true);
            setSearchSuggestions([]);
        }
        const clusteredStartedAt = performance.now();
        getClusteredChargingLocations({
            filters,
            center: nextCenter,
            bounds: nextBounds,
            zoomLevel: nextZoom,
        })
            .then((response) => {
                const clusteredDuration = performance.now() - clusteredStartedAt;
                const parsedItems = transformStations(response);
                const parsedStations = addOffsetToDuplicateCoordinates(
                    parsedItems.filter((item: any) => !item.properties?.cluster),
                    0.0005
                );
                setMapItems([...parsedItems.filter((item: any) => item.properties?.cluster), ...parsedStations]);
                updateStations(parsedStations);

                if (sidebarMode === 'cluster-order' && parsedStations.length === 0) {
                    sidebarInitializedRef.current = true;
                    sidebarPopulationModeRef.current = sidebarMode;
                    initialSidebarOrderRef.current = [];
                    sidebarBatchIdRef.current += 1;
                    setStationDetailsById({});
                    setStationDetailsLoadingById({});
                    stationDetailsByIdRef.current = {};
                    stationDetailsLoadingByIdRef.current = {};
                    sidebarBatchStartedAtRef.current = null;
                    sidebarDetailsDurationRef.current = 0;
                    sidebarDetailsCompletedCountRef.current = 0;
                    sidebarDetailsTargetCountRef.current = 0;
                    setBaseSidebarStations([]);
                    setSelectedSidebarStation(null);
                    setExpandedSidebarStationId(null);
                    setOpenMarker(null);
                    clearRoute(directionsRendererRef.current);
                    setShowLocationSearchEmptyState(true);
                    return;
                }

                if (sidebarMode !== 'preserve') {
                    refreshSidebarStationsController(parsedStations, sidebarMode, nextCenter);
                }
            })
            .catch((error) => {
                console.error(error);
                if (hasSearch) {
                    setSearchSuggestions([]);
                    setHighlightedSuggestionIndex(-1);
                }
            })
            .finally(() => {
                if (hasSearch) {
                    setIsSearchLoading(false);
                }
            });
    };

    const debouncedSearch = useMemo(
        () =>
            debounce((value: string) => {
                fetchGoogleSuggestions({
                    value,
                    maps: mapsRef.current,
                    placesAutocompleteServiceRef,
                    autocompleteRequestIdRef,
                    setSearchSuggestions,
                    setIsSearchLoading,
                    setHighlightedSuggestionIndex,
                });
            }, 300),
        []
    );

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchValue(value);
        setShowSearchSuggestions(!!value.trim());
        setHighlightedSuggestionIndex(-1);
        if (!value.trim()) {
            setSearchSuggestions([]);
            setIsSearchLoading(false);
            setHighlightedSuggestionIndex(-1);
        }
        debouncedSearch(value);
    };

    const resetSearch = async () => {
        setSearchValue('');
        setSearchSuggestions([]);
        setShowSearchSuggestions(false);
        setHighlightedSuggestionIndex(-1);
        setOpenMarker(null);
        setExpandedSidebarStationId(null);
        setShowLocationSearchEmptyState(false);
        setSearchPinLocation(null);
        requestZoomOverrideRef.current = null;
        clearRoute(directionsRendererRef.current);

        const resolvedUserLocation = userLocation ?? await requestUserLocation();
        const fallbackCenter = resolvedUserLocation ?? MAP_LOCATIONS.Bucharest;
        const fallbackZoom = resolvedUserLocation ? 13 : BUCHAREST_FALLBACK_ZOOM;

        applyViewport({
            nextCenter: fallbackCenter,
            nextZoom: fallbackZoom,
            setCenter,
            setZoom,
            map: mapRef.current,
        });
        runManualViewportUpdate({
            nextCenter: fallbackCenter,
            nextBounds: undefined,
            requestZoom: fallbackZoom,
            sidebarMode: 'nearest',
            skipViewportRefreshCountRef,
            requestZoomOverrideRef,
            updateMarkers: (search, nextCenterArg, nextBoundsArg, nextZoomArg, sidebarModeArg) => {
                updateMarkers(search, appliedFiltersRef.current, nextCenterArg, nextBoundsArg, nextZoomArg, sidebarModeArg);
            },
        });
    };

    const handleSearchFocus = () => {
        if (searchBlurTimeoutRef.current) {
            clearTimeout(searchBlurTimeoutRef.current);
            searchBlurTimeoutRef.current = null;
        }
        setShowSearchSuggestions(!!searchValue.trim());
        if (searchSuggestions.length > 0) {
            setHighlightedSuggestionIndex(0);
        }
    };

    const handleSearchBlur = () => {
        searchBlurTimeoutRef.current = setTimeout(() => {
            setShowSearchSuggestions(false);
        }, 120);
    };

    const selectSuggestion = async (suggestion: SearchSuggestion) => {
        setSearchValue(suggestion.label);
        setShowSearchSuggestions(false);
        setHighlightedSuggestionIndex(-1);

        if (suggestion.placePrediction) {
            try {
                const place = suggestion.placePrediction.toPlace();
                const {place: placeDetails} = await place.fetchFields({
                    fields: ['location', 'viewport', 'types'],
                });
                const nextZoom = getSearchZoomForTypes(placeDetails.types ?? []);
                const placeLocation = placeDetails.location
                    ? {
                        lat: placeDetails.location.lat(),
                        lng: placeDetails.location.lng(),
                    }
                    : null;

                if (placeLocation) {
                    const didApplySearchResult = await applySearchResult({
                        nextCenter: placeLocation,
                        requestZoom: nextZoom,
                        setCenter,
                        setBounds,
                        setZoom,
                        setMapSearchViewport,
                        runManualViewportUpdate: (nextCenterArg, nextBoundsArg, nextZoomArg, sidebarModeArg) =>
                            runManualViewportUpdate({
                                nextCenter: nextCenterArg,
                                nextBounds: nextBoundsArg,
                                requestZoom: nextZoomArg,
                                sidebarMode: sidebarModeArg,
                                skipViewportRefreshCountRef,
                                requestZoomOverrideRef,
                                updateMarkers: (search, centerArg, boundsArg, zoomArg, modeArg) => {
                                    updateMarkers(search, appliedFiltersRef.current, centerArg, boundsArg, zoomArg, modeArg);
                                },
                            }),
                    });
                    if (didApplySearchResult) {
                        setSearchPinLocation(placeLocation);
                        return;
                    }
                }

                if (placeDetails.viewport) {
                    const viewportCenter = placeDetails.viewport.getCenter();
                    const nextCenter = {lat: viewportCenter.lat(), lng: viewportCenter.lng()};
                    const didApplySearchResult = await applySearchResult({
                        nextCenter,
                        requestZoom: nextZoom,
                        setCenter,
                        setBounds,
                        setZoom,
                        setMapSearchViewport,
                        runManualViewportUpdate: (nextCenterArg, nextBoundsArg, nextZoomArg, sidebarModeArg) =>
                            runManualViewportUpdate({
                                nextCenter: nextCenterArg,
                                nextBounds: nextBoundsArg,
                                requestZoom: nextZoomArg,
                                sidebarMode: sidebarModeArg,
                                skipViewportRefreshCountRef,
                                requestZoomOverrideRef,
                                updateMarkers: (search, centerArg, boundsArg, zoomArg, modeArg) => {
                                    updateMarkers(search, appliedFiltersRef.current, centerArg, boundsArg, zoomArg, modeArg);
                                },
                            }),
                    });
                    if (didApplySearchResult) {
                        setSearchPinLocation(nextCenter);
                        return;
                    }
                }
            } catch (error) {
                console.warn('Autocomplete place selection failed:', error);
            }
        }

        const geocodedLocation = await geocodeSearchSelection({
            label: suggestion.label,
            maps: mapsRef.current,
        });
        const geocodedZoom = getSearchZoomForTypes(geocodedLocation?.types ?? []);
        if (geocodedLocation) {
            const didApplySearchResult = await applySearchResult({
                nextCenter: geocodedLocation.center,
                requestZoom: geocodedZoom,
                setCenter,
                setBounds,
                setZoom,
                setMapSearchViewport,
                runManualViewportUpdate: (nextCenterArg, nextBoundsArg, nextZoomArg, sidebarModeArg) =>
                    runManualViewportUpdate({
                        nextCenter: nextCenterArg,
                        nextBounds: nextBoundsArg,
                        requestZoom: nextZoomArg,
                        sidebarMode: sidebarModeArg,
                        skipViewportRefreshCountRef,
                        requestZoomOverrideRef,
                        updateMarkers: (search, centerArg, boundsArg, zoomArg, modeArg) => {
                            updateMarkers(search, appliedFiltersRef.current, centerArg, boundsArg, zoomArg, modeArg);
                        },
                    }),
            });
            if (didApplySearchResult) {
                setSearchPinLocation(geocodedLocation.center);
                return;
            }
        }

        runManualViewportUpdate({
            nextCenter: center,
            nextBounds: undefined,
            requestZoom: zoom,
            sidebarMode: 'cluster-order',
            skipViewportRefreshCountRef,
            requestZoomOverrideRef,
            updateMarkers: (search, nextCenterArg, nextBoundsArg, nextZoomArg, sidebarModeArg) => {
                updateMarkers(search, appliedFiltersRef.current, nextCenterArg, nextBoundsArg, nextZoomArg, sidebarModeArg);
            },
        });
    };

    const currentFilterPayload = getFilterPayload({
        showAvailability,
        showStandard,
        showRapid,
        showType2,
        showCcsCombo2,
        showChademo,
    });

    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showSearchSuggestions || searchSuggestions.length === 0) {
            if (event.key === 'Escape') {
                setShowSearchSuggestions(false);
            }
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightedSuggestionIndex((current) =>
                current < searchSuggestions.length - 1 ? current + 1 : current
            );
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedSuggestionIndex((current) => (current > 0 ? current - 1 : 0));
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            const selected = searchSuggestions[highlightedSuggestionIndex];
            if (selected) {
                selectSuggestion(selected);
                return;
            }

            void (async () => {
                const geocodedLocation = await geocodeSearchSelection({
                    label: searchValue,
                    maps: mapsRef.current,
                });
                if (!geocodedLocation) return;
                const geocodedZoom = getSearchZoomForTypes(geocodedLocation.types);
                const didApplySearchResult = await applySearchResult({
                    nextCenter: geocodedLocation.center,
                    requestZoom: geocodedZoom,
                    setCenter,
                    setBounds,
                    setZoom,
                    setMapSearchViewport,
                    runManualViewportUpdate: (nextCenterArg, nextBoundsArg, nextZoomArg, sidebarModeArg) =>
                        runManualViewportUpdate({
                            nextCenter: nextCenterArg,
                            nextBounds: nextBoundsArg,
                            requestZoom: nextZoomArg,
                            sidebarMode: sidebarModeArg,
                            skipViewportRefreshCountRef,
                            requestZoomOverrideRef,
                            updateMarkers: (search, centerArg, boundsArg, zoomArg, modeArg) => {
                                updateMarkers(search, appliedFiltersRef.current, centerArg, boundsArg, zoomArg, modeArg);
                            },
                        }),
                });
                if (!didApplySearchResult) return;
                setSearchPinLocation(geocodedLocation.center);
                setShowSearchSuggestions(false);
            })();
            return;
        }

        if (event.key === 'Escape') {
            setShowSearchSuggestions(false);
            setHighlightedSuggestionIndex(-1);
        }
    };

    const resetFilters = () => {
        setShowAvailability(false);
        setShowStandard(false);
        setShowRapid(false);
        setShowType2(false);
        setShowCcsCombo2(false);
        setShowChademo(false);
        const clearedFilters: ChargingLocationFilters = {
            availability: false,
            standard: false,
            rapid: false,
            type2: false,
            ccsCombo2: false,
            chademo: false
        };
        setAppliedFilters(clearedFilters);
        updateMarkers(
            searchValue,
            clearedFilters,
            center,
            bounds,
            requestZoomOverrideRef.current ?? zoom,
            sidebarPopulationModeRef.current
        );
    };

    const applyFilters = () => {
        const nextFilters = currentFilterPayload;
        setAppliedFilters(nextFilters);
        updateMarkers(
            searchValue,
            nextFilters,
            center,
            bounds,
            requestZoomOverrideRef.current ?? zoom,
            sidebarPopulationModeRef.current
        );
        setIsFilterDrawerOpen(false);
    };

    const handleTopBarAvailabilityToggle = () => {
        const nextAvailability = !showAvailability;
        setShowAvailability(nextAvailability);
        const nextFilters = getFilterPayloadWithOverrides(currentFilterPayload, {availability: nextAvailability});
        setAppliedFilters(nextFilters);
        updateMarkers(
            searchValue,
            nextFilters,
            center,
            bounds,
            requestZoomOverrideRef.current ?? zoom,
            sidebarPopulationModeRef.current
        );
    };

    const handleTopBarRapidToggle = () => {
        const nextStandard = !showStandard;
        setShowStandard(nextStandard);
        const nextFilters = getFilterPayloadWithOverrides(currentFilterPayload, {standard: nextStandard});
        setAppliedFilters(nextFilters);
        updateMarkers(
            searchValue,
            nextFilters,
            center,
            bounds,
            requestZoomOverrideRef.current ?? zoom,
            sidebarPopulationModeRef.current
        );
    };

    useEffect(() => {
        if (!bounds || !isInitialViewportResolved) return;

        if (skipViewportRefreshCountRef.current > 0) {
            skipViewportRefreshCountRef.current -= 1;
            return;
        }

        const timeoutId = window.setTimeout(() => {
            updateMarkers(
                searchValue,
                appliedFiltersRef.current,
                center,
                bounds,
                requestZoomOverrideRef.current ?? zoom
            );
        }, 250);

        return () => window.clearTimeout(timeoutId);
    }, [center, bounds, zoom, isInitialViewportResolved]);

    const sidebarStations = useMemo(() => baseSidebarStations, [baseSidebarStations]);

    const activeSidebarStation = useMemo(() => {
        if (expandedSidebarStationId === null) return null;

        const stationFromSidebar = sidebarStations.find((station) => {
            const stationId = station.properties?.id ?? station.properties?.data?.id;
            return stationId === expandedSidebarStationId;
        });

        if (stationFromSidebar) {
            return stationFromSidebar;
        }

        const selectedStationId = selectedSidebarStation?.properties?.id ?? selectedSidebarStation?.properties?.data?.id;
        if (selectedStationId === expandedSidebarStationId) {
            return selectedSidebarStation;
        }

        return null;
    }, [expandedSidebarStationId, sidebarStations, selectedSidebarStation]);

    useDinamicMapUiEffects({
        debouncedSearch,
        appliedFilters,
        appliedFiltersRef,
        stationDetailsById,
        stationDetailsByIdRef,
        stationDetailsLoadingById,
        stationDetailsLoadingByIdRef,
        mapRef,
        mapsRef,
        searchPinLocation,
        searchPinMarkerRef,
        searchBlurTimeoutRef,
        directionsRendererRef,
        openMarker,
        stations,
        setCenter,
        setZoom,
        expandedSidebarStationId,
        baseSidebarStations,
        selectedSidebarStation,
        sidebarCardRefs,
        sidebarListRef,
        searchSuggestions,
        highlightedSuggestionIndex,
        setHighlightedSuggestionIndex,
        showSearchSuggestions,
        suggestionRefs,
        activeSidebarStation,
    });

    const distanceReferenceContext = getDistanceReferenceContext({
        searchPinLocation,
        userLocation,
        fallbackPoint: geolocationFallbackCenter,
    });

    return (
        <div className={classnames(style.mapContainer, style.maps)}>
            <MapTopBar
                search={(
                    <MapSearchBar
                        searchValue={searchValue}
                        isSearchLoading={isSearchLoading}
                        showSearchSuggestions={showSearchSuggestions}
                        searchSuggestions={searchSuggestions}
                        highlightedSuggestionIndex={highlightedSuggestionIndex}
                        onSearchChange={handleSearchChange}
                        onSearchFocus={handleSearchFocus}
                        onSearchBlur={handleSearchBlur}
                        onSearchKeyDown={handleSearchKeyDown}
                        onResetSearch={resetSearch}
                        onSuggestionMouseEnter={setHighlightedSuggestionIndex}
                        onSuggestionSelect={selectSuggestion}
                        suggestionRef={(index, element) => {
                            suggestionRefs.current[index] = element;
                        }}
                    />
                )}
                isRapidActive={!!appliedFilters.standard}
                isAvailabilityActive={!!appliedFilters.availability}
                isFilterDrawerOpen={isFilterDrawerOpen}
                onRapidToggle={handleTopBarRapidToggle}
                onAvailabilityToggle={handleTopBarAvailabilityToggle}
                onFilterToggle={() => setIsFilterDrawerOpen((isOpen) => !isOpen)}
            />

            <div className={style.mapStage}>
                {isFilterDrawerOpen && (
                    <FilterDrawer
                        showAvailability={showAvailability}
                        setShowAvailability={setShowAvailability}
                        showStandard={showStandard}
                        setShowStandard={setShowStandard}
                        showRapid={showRapid}
                        setShowRapid={setShowRapid}
                        showType2={showType2}
                        setShowType2={setShowType2}
                        showCcsCombo2={showCcsCombo2}
                        setShowCcsCombo2={setShowCcsCombo2}
                        showChademo={showChademo}
                        setShowChademo={setShowChademo}
                        chargerType2Src={chargerType2.src}
                        chargerCcsCombo2Src={chargerCcsCombo2.src}
                        chargerChademoSrc={chargerChademo.src}
                        onClose={() => setIsFilterDrawerOpen(false)}
                        onReset={resetFilters}
                        onApply={applyFilters}
                    />
                )}

                <GoogleMapReact
                    yesIWantToUseGoogleMapApiInternals
                    bootstrapURLKeys={{key: GOOGLE_MAP_API_KEY, libraries: ['places']}}
                    defaultCenter={DEFAULT_CENTER}
                    defaultZoom={DEFAULT_ZOOM}
                    zoom={zoom}
                    center={center}
                    options={(maps) => ({
                        styles: MAP_STYLES,
                        maxZoom: MAX_ZOOM,
                        disableDefaultUI: true,
                        disableDoubleClickZoom: true,
                        clickableIcons: false,
                        zoomControl: true,
                        zoomControlOptions: {position: maps.ControlPosition.TOP_RIGHT}
                    })}
                    onChange={(event) =>
                        handleMapChange({
                            event,
                            map: mapRef.current,
                            defaultCenter: DEFAULT_CENTER,
                            setBounds,
                            setZoom,
                            setCenter,
                        })
                    }
                    onGoogleApiLoaded={({map, maps}) => {
                        mapRef.current = map;
                        mapsRef.current = maps;
                        initializeMap({
                            map,
                            fallbackCenter: geolocationFallbackCenter,
                            fallbackZoom: BUCHAREST_FALLBACK_ZOOM,
                            setCenter,
                            setZoom,
                            setBounds,
                            setUserLocation,
                            setIsInitialViewportResolved,
                        });
                    }}
                >
                    {mapItems.map((cluster) => (
                        <Marker
                            key={cluster.properties?.data?.id ?? `cluster-${cluster.properties?.id}`}
                            cluster={cluster}
                            openMarker={openMarker}
                            setOpenMarker={setOpenMarker}
                            handleMarkerSelect={() => void handleStationSelectController(cluster)}
                            handleClusterZoom={() =>
                                handleClusterZoom({
                                    cluster,
                                    zoom,
                                    maxZoom: MAX_ZOOM,
                                    setCenter,
                                    setZoom,
                                    map: mapRef.current,
                                })
                            }
                            // Required by <GoogleMapReact> even if aren't used in <Marker>
                            lng={cluster.geometry.coordinates[0]}
                            lat={cluster.geometry.coordinates[1]}
                        />
                    ))}
                </GoogleMapReact>

                <aside className={style.mapSidebar}>
                    <div ref={sidebarListRef} className={style.stationList}>
                        {showLocationSearchEmptyState && (
                            <EmptySidebarState />
                        )}
                        {sidebarStations.map((station) => {
                            const data = station.properties?.data;
                            if (!data) return null;
                            const stationId = station.properties?.id ?? data.id;
                            const isActive = stationId === expandedSidebarStationId;
                            const googleMapsUrl = getGoogleMapsDirectionsUrl(
                                station,
                                searchPinLocation ?? userLocation
                            );
                            const statusLabel =
                                STATUS_LABEL[data.status as keyof typeof STATUS_LABEL] ?? 'Defect';

                            return (
                                <StationCard
                                    key={stationId}
                                    cardRef={(element) => {
                                        sidebarCardRefs.current[String(stationId)] = element;
                                    }}
                                    station={station}
                                    stationIconSrc={stationIconAsset.src}
                                    isActive={isActive}
                                    googleMapsUrl={googleMapsUrl}
                                    statusLabel={statusLabel}
                                    statusClassName={getStatusClassName(data.status, {
                                        available: style.stationStatusAvailable,
                                        busy: style.stationStatusBusy,
                                        default: style.stationStatusDefault,
                                    })}
                                    distanceReferenceLabel={distanceReferenceContext.label}
                                    distanceLabel={getDistanceLabel({
                                        station,
                                        searchPinLocation,
                                        userLocation,
                                        fallbackPoint: geolocationFallbackCenter,
                                    })}
                                    onSelect={() => {
                                        void handleStationSelectController(station);
                                    }}
                                    onDeselect={() => {
                                        setExpandedSidebarStationId(null);
                                        setOpenMarker(null);
                                        setSelectedSidebarStation(null);
                                    }}
                                    onClose={() => {
                                        setExpandedSidebarStationId(null);
                                        setOpenMarker(null);
                                        clearRoute(directionsRendererRef.current);
                                    }}
                                    getConnectorIcon={(connectorType) =>
                                        getConnectorIcon(connectorType, {
                                            type2: chargerType2,
                                            ccs: chargerCcsCombo2,
                                            chademo: chargerChademo,
                                        })
                                    }
                                />
                            );
                        })}
                        {sidebarStations.length === 0 && Object.keys(stationDetailsLoadingById).length > 0 && (
                            Array.from({length: 3}).map((_, index) => (
                                <div key={`station-skeleton-${index}`} className={style.stationCard}>
                                    <Skeleton
                                        active
                                        title={{width: '60%'}}
                                        paragraph={{rows: 4, width: ['45%', '80%', '55%', '65%']}}
                                    />
                                </div>
                            ))
                        )}
                    </div>
                </aside>

                {activeSidebarStation?.properties?.data && (
                    <StationModal
                        station={activeSidebarStation}
                        stationIconSrc={stationIconAsset.src}
                        googleMapsUrl={getGoogleMapsDirectionsUrl(
                            activeSidebarStation,
                            searchPinLocation ?? userLocation
                        )}
                        statusLabel={
                            STATUS_LABEL[
                                activeSidebarStation.properties?.data?.status as keyof typeof STATUS_LABEL
                            ] ?? 'Defect'
                        }
                        statusClassName={getStatusClassName(
                            activeSidebarStation.properties?.data?.status,
                            {
                                available: style.stationStatusAvailable,
                                busy: style.stationStatusBusy,
                                default: style.stationStatusDefault,
                            }
                        )}
                        distanceReferenceLabel={distanceReferenceContext.label}
                        distanceLabel={getDistanceLabel({
                            station: activeSidebarStation,
                            searchPinLocation,
                            userLocation,
                            fallbackPoint: geolocationFallbackCenter,
                        })}
                        onClose={() => {
                            setExpandedSidebarStationId(null);
                            setOpenMarker(null);
                            clearRoute(directionsRendererRef.current);
                        }}
                        getConnectorIcon={(connectorType) =>
                            getConnectorIcon(connectorType, {
                                type2: chargerType2,
                                ccs: chargerCcsCombo2,
                                chademo: chargerChademo,
                            })
                        }
                    />
                )}

            </div>
        </div>
    );
};

export default DinamicMap;
